import axios from 'axios';

/**
 * Synchronously resolve the param_token. Prefers the URL `?token=` value
 * (which is authoritative for the current navigation) and falls back to the
 * value persisted in localStorage. When the URL carries a token that hasn't
 * yet been written to localStorage by AppContent's useEffect, this prevents
 * a race where early click handlers (e.g. "Got it" -> analysis_started) fire
 * a tracking event with no token, leaving the backend unable to associate an
 * email with the event.
 *
 * Also persists the URL token to localStorage so subsequent calls / requests
 * see it without depending on render ordering.
 */
const resolveParamTokenSync = () => {
  let urlToken = null;
  try {
    const sp = new URLSearchParams(window.location.search);
    // `param_token` is an alias used by links from the agent platform
    // (vendorleads-agent) so an agent previewing their hub arrives logged in.
    urlToken = sp.get('token') || sp.get('param_token');
  } catch (_) { /* ignore */ }
  let stored = null;
  try {
    stored = localStorage.getItem('param_token');
  } catch (_) { /* ignore */ }
  if (urlToken && urlToken !== stored) {
    try { localStorage.setItem('param_token', urlToken); } catch (_) { /* ignore */ }
  }
  return urlToken || stored || null;
};

/**
 * Best-effort detection of bots / crawlers / headless browsers so we don't
 * pollute production.events. Conservative: anything ambiguous returns true.
 */
const BOT_UA_RE = /(bot\b|crawler|spider|crawling|slurp|bingpreview|facebookexternalhit|whatsapp|telegrambot|twitterbot|linkedinbot|embedly|quora|pinterest|skypeuripreview|nuzzel|discordbot|adsbot-google|mediapartners-google|google web preview|googleother|gptbot|chatgpt-user|oai-searchbot|claudebot|anthropic-ai|perplexitybot|youbot|applebot|duckduckbot|baiduspider|yandexbot|sogou|exabot|ia_archiver|archive\.org_bot|semrushbot|ahrefsbot|mj12bot|dotbot|petalbot|seznambot|screaming frog|netcraftsurvey|datadog|pingdom|uptimerobot|statuscake|monitor\b|headlesschrome|phantomjs|puppeteer|playwright|selenium|webdriver|lighthouse|chrome-lighthouse|pagespeed|gtmetrix)/i;

export const isLikelyBot = () => {
  try {
    if (typeof navigator === 'undefined') return true;
    if (navigator.webdriver === true) return true;
    const ua = navigator.userAgent || '';
    if (!ua) return true;
    if (BOT_UA_RE.test(ua)) return true;
  } catch (_) { /* ignore */ }
  return false;
};

/**
 * Fire-and-forget event tracking. Non-blocking — never throws or awaits.
 * @param {string} eventName - e.g. 'click_cta', 'click_sign_in'
 * @param {object} [meta] - optional extra data (agent_slug, property_id, etc.)
 */
export const trackEvent = (eventName, meta = {}) => {
  try {
    if (isLikelyBot()) return;
    const storedParamToken = resolveParamTokenSync();
    let smsCampaign = null;
    try { smsCampaign = localStorage.getItem('sms_campaign'); } catch (_) { /* ignore */ }
    // Resolve agent_slug from (in order): meta override, ?agent= URL param,
    // sessionStorage-cached agentDetails (set by useAgentDetails — covers
    // custom-domain users that have no ?agent= param), or the cached slug
    // key. Without this fallback, events fired on custom domains (or after
    // first navigation away from the landing URL) end up with NULL
    // agent_slug and never appear in the activity modal, which scopes by
    // agent_slug.
    let cachedSlug = null;
    try {
      const stored = sessionStorage.getItem('agentDetails');
      if (stored) {
        const parsed = JSON.parse(stored);
        cachedSlug = parsed?.landing_page_slug || null;
      }
      if (!cachedSlug) {
        const key = sessionStorage.getItem('landing_page_slug');
        // Only use the cacheKey when it looks like a slug (not a domain).
        if (key && !key.includes('.') && !key.includes('/')) cachedSlug = key;
      }
    } catch (_) { /* ignore */ }
    const urlSlug = (() => {
      try { return new URLSearchParams(window.location.search).get('agent'); }
      catch (_) { return null; }
    })();
    const body = JSON.stringify({
      event_name: eventName,
      source: window.location.href,
      param_token: storedParamToken,
      sms_campaign: smsCampaign || undefined,
      agent_slug: meta.agent_slug || urlSlug || cachedSlug || undefined,
      ...meta,
    });
    const url = `${window.api_host}/save-settings`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
    } else {
      fetch(url, { method: 'POST', body, headers: { 'Content-Type': 'text/plain' }, keepalive: true }).catch(() => {});
    }
  } catch (_) { /* non-blocking */ }
};

/**
 * Fire-and-forget POST that survives tab close via keepalive.
 * Returns immediately — never blocks, never throws.
 */
export const fireAndForgetPost = (url, data) => {
  try {
    const jwt_token = localStorage.getItem('jwt_token');
    const storedParamToken = resolveParamTokenSync();
    let smsCampaign = null;
    try { smsCampaign = localStorage.getItem('sms_campaign'); } catch (_) { /* ignore */ }
    const payload = JSON.stringify({
      ...data,
      param_token: storedParamToken,
      sms_campaign: smsCampaign || undefined,
      source: window.location.href,
    });
    const headers = { 'Content-Type': 'application/json' };
    if (jwt_token) {
      headers['Authorization'] = `Bearer ${jwt_token}`;
    }
    fetch(url, {
      method: 'POST',
      headers,
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* non-blocking */ }
};

// Safe localStorage helpers — iOS Safari in Private mode and some in-app
// email browsers throw QuotaExceededError / SecurityError on access.
const safeLocalGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
};
const safeLocalSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    // Ignore — storage may be unavailable (private mode, in-app browsers, etc.)
  }
};

export const handleTokenAndPostRequest = async (url, data, headers = {}) => {
  try {
    // Get 'jwt_token' from local storage
    let jwt_token = safeLocalGet('jwt_token');

    // Get 'token' from URL query parameters ('param_token' is the alias used
    // by agent-platform links so a previewing agent arrives logged in).
    const urlParams = new URLSearchParams(window.location.search);
    const param_token = urlParams.get('token') || urlParams.get('param_token');

    // If a token exists in URL, persist it for later requests (best-effort)
    if (param_token) {
      safeLocalSet('param_token', param_token);
    }

    // Prefer the URL token directly so flows like /unsubscribe work
    // even when localStorage is unavailable (iOS Safari private mode, etc.)
    const storedParamToken = param_token || safeLocalGet('param_token');

    // Check if data is FormData
    if (data instanceof FormData) {
      // Append additional fields to FormData
      data.append('param_token', storedParamToken);
      data.append('source', window.location.href);
    } else {
      // Handle non-FormData cases
      data = {
        ...data,
        param_token: storedParamToken,
        source: window.location.href,
      };
    }

    // Add 'Authorization' header only if JWT token exists
    const requestHeaders = {
      ...headers,
    };
    if (jwt_token) {
      requestHeaders['Authorization'] = `Bearer ${jwt_token}`;
    }

    console.log('Request Headers:', requestHeaders);
    // Make the POST request
    const config = { headers: requestHeaders };
    if (headers?.signal) {
      config.signal = headers.signal;
    }
    const response = await axios.post(url, data, config);

    return response;
  } catch (error) {
    console.error('Error:', error);
    trackEvent('api_error', {
      endpoint: url,
      status: error?.response?.status,
      message: error?.response?.data?.error || error?.response?.data?.message || error?.message,
      response_data: typeof error?.response?.data === 'object' ? JSON.stringify(error.response.data) : error?.response?.data,
    });
    throw error;
  }
};

export const formatCurrency = (value) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  const roundedValue = Math.round(value);
  if (roundedValue >= 1000000) {
    return `$${(roundedValue / 1000000).toFixed(2)}m`; // Format as millions
  } else if (roundedValue >= 10000) {
    return `$${(roundedValue / 1000).toFixed(0)}k`; // Format as thousands
  } else {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,  // Ensure no decimal places are displayed
      maximumFractionDigits: 0 
    }).format(roundedValue); // Format as hundreds
  }
};



export const setMetaTags = ({ title, description, keywords, url, image }) => {
  document.title = title;

  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'description';
    meta.content = description;
    document.head.appendChild(meta);
  }

  const metaKeywords = document.querySelector('meta[name="keywords"]');
  if (metaKeywords) {
    metaKeywords.setAttribute('content', keywords);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'keywords';
    meta.content = keywords;
    document.head.appendChild(meta);
  }

  const metaOgTitle = document.querySelector('meta[property="og:title"]');
  if (metaOgTitle) {
    metaOgTitle.setAttribute('content', title);
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:title');
    meta.content = title;
    document.head.appendChild(meta);
  }

  const metaOgDescription = document.querySelector('meta[property="og:description"]');
  if (metaOgDescription) {
    metaOgDescription.setAttribute('content', description);
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:description');
    meta.content = description;
    document.head.appendChild(meta);
  }

  const metaOgUrl = document.querySelector('meta[property="og:url"]');
  if (metaOgUrl) {
    metaOgUrl.setAttribute('content', url);
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:url');
    meta.content = url;
    document.head.appendChild(meta);
  }

  const metaOgImage = document.querySelector('meta[property="og:image"]');
  if (metaOgImage) {
    metaOgImage.setAttribute('content', image);
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:image');
    meta.content = image;
    document.head.appendChild(meta);
  }

  const metaTwitterTitle = document.querySelector('meta[name="twitter:title"]');
  if (metaTwitterTitle) {
    metaTwitterTitle.setAttribute('content', title);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'twitter:title';
    meta.content = title;
    document.head.appendChild(meta);
  }

  const metaTwitterDescription = document.querySelector('meta[name="twitter:description"]');
  if (metaTwitterDescription) {
    metaTwitterDescription.setAttribute('content', description);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'twitter:description';
    meta.content = description;
    document.head.appendChild(meta);
  }

  const metaTwitterImage = document.querySelector('meta[name="twitter:image"]');
  if (metaTwitterImage) {
    metaTwitterImage.setAttribute('content', image);
  } else {
    const meta = document.createElement('meta');
    meta.name = 'twitter:image';
    meta.content = image;
    document.head.appendChild(meta);
  }
};

